import { PayloadAction } from '@reduxjs/toolkit';
import { EDSV2ArrayResponse, EDSV2Response } from '@/types/eds/api/responses/EDSV2Response';
import { NormalizedV2Response } from '@/types/eds/api/responses/NormalizedV2Response';
import { AddAddressRequest } from '@/domains/addresses/types/api/requests/AddAddressRequest';
import { HttpServiceResponse } from '@/services/HttpService/types';
import { call, put, select, takeLatest } from 'redux-saga/effects';
import { EdsEntityCodes } from '@/features/eds/enums/EdsEntityCodes';
import EdsAdapterService from '@/features/eds/services/EdsAdapterService/EdsAdapterService';
import EdsDebugService from '@/features/eds/services/EdsDebugService/EdsDebugService';
import { resetGeographyState } from '@/features/geography/store/geographySlice';
import { resetAddressValidationState } from '@/features/googleApi/addressValidationApi/store/googleAddressValidationApiSlice';
import { resetGooglePlacesState } from '@/features/googleApi/placesApi/store/googlePlacesApiSlice';
import { Modals } from '@/features/serviceModal/enums/Modals';
import { SnackType } from '@/features/snacks/enums/SnackType';
import { setSystemLoadingStatus } from '@/features/system/store/systemSlice';
import {
  addPartyAddress,
  getPartyAddresses,
  getPartyAddressesSuccess,
  getPersonOrBusinessData,
  getPersonOrBusinessDataSuccess,
  selectPartyOrBusinessId,
  selectPartyOrBusinessType,
} from '@/domains/partyDetails/store/partyDetails.slice';
import HttpService from '@/services/HttpService/HttpService';
import ModalService from '@/services/ModalService/ModalService';
import SnacksService from '@/services/SnackService/SnackService';

function* getPersonOrBusinessDataSaga({ payload }: PayloadAction<{ partyId: string }>) {
  const { partyId } = payload;
  try {
    const { data }: HttpServiceResponse<EDSV2Response> = yield call(
      HttpService.get,
      `/v2/Party/${partyId}?includeEntities=Person.ContactInformation,Business.ContactInformation`,
      {},
      {
        baseURL: import.meta.env.VITE_API_URL_EDS,
      },
    );

    const personOrBusinessData = data.personId ? data.person : data.business;
    const personOrBusinessNormalizedData: NormalizedV2Response = yield call(
      EdsAdapterService.normalize,
      personOrBusinessData as EDSV2Response,
      {
        section: 'PartyPage',
      },
    );

    EdsDebugService.logResponse(data.personId ? EdsEntityCodes.Person : EdsEntityCodes.Business, {
      rawResponse: personOrBusinessData,
      normalizedResponse: personOrBusinessNormalizedData,
      dataBySlot: personOrBusinessNormalizedData
        ? EdsAdapterService.generateSlotRecord(personOrBusinessNormalizedData)
        : {},
    });

    yield put(getPersonOrBusinessDataSuccess({ personOrBusinessNormalizedData }));
  } catch (error) {
    console.error('Error fetching person or business data:', error);
  }
}

function* getPartyAddressesSaga() {
  try {
    const personOrBusinessId = yield select(selectPartyOrBusinessId);
    const personOrBusinessType = yield select(selectPartyOrBusinessType);

    const { data }: HttpServiceResponse<{ listings: EDSV2ArrayResponse }> = yield call(
      HttpService.post,
      `/filter/Address?page=1&perPage=1000&includes=AddressTypes.AddressType`,
      [
        {
          propertyName: personOrBusinessType === EdsEntityCodes.Person ? 'PersonId' : 'BusinessId',
          values: [String(personOrBusinessId)],
        },
      ],
      {
        baseURL: import.meta.env.VITE_API_URL_EDS,
      },
    );

    const normalizedData: NormalizedV2Response[] = yield call(EdsAdapterService.normalize, data.listings);

    EdsDebugService.logResponse(EdsEntityCodes.Addresses, {
      rawResponse: data,
      normalizedResponse: normalizedData,
    });

    yield put(getPartyAddressesSuccess({ addressList: normalizedData }));
  } catch (error) {
    console.error('Error fetching addresses data:', error);
  }
}

function* addPartyAddressSaga({ payload }: PayloadAction<AddAddressRequest>) {
  try {
    yield put(setSystemLoadingStatus(true));
    yield call(HttpService.post, `/examiner/address/add-address`, payload);
    SnacksService.add({
      type: SnackType.Success,
      title: 'Updates saved successfully!',
      anchorOrigin: {
        horizontal: 'right',
        vertical: 'bottom',
      },
    });

    yield put(getPartyAddresses());
  } catch (error) {
    console.error(error);
  } finally {
    yield put(setSystemLoadingStatus(false));
    yield put(resetGeographyState());
    yield put(resetAddressValidationState());
    yield put(resetGooglePlacesState());
    ModalService.close(Modals.AddAddress);
  }
}

export function* partyDetailsWatcher() {
  yield takeLatest([getPersonOrBusinessData.type], getPersonOrBusinessDataSaga);
  yield takeLatest([getPartyAddresses.type], getPartyAddressesSaga);
  yield takeLatest([addPartyAddress.type], addPartyAddressSaga);
}
